/**
 * Avatar 스타일
 * @description 컴포넌트 토큰이 없어 시맨틱으로 조립한다. 앱 `Avatar.styles.ts` 와 같은 표.
 *
 *   크기(24·32·48·80·104)는 시안 값이다. 아바타의 size 이름과 `size.control` 의 이름은
 *   서로 무관해서 값으로 찾아 쓴다 — `sm` 이 스케일의 `lg`, `md` 가 `xxxxl` 이다.
 *
 *   square 의 모서리는 폭의 약 1/6 — xs·xssm 4 · sm 8 · md 12 · xl 16 으로 크기와 짝지어
 *   정해진다. 한 값으로 고정하면 24 짜리는 원처럼, 104 짜리는 각져 보인다.
 */
import { cva } from "class-variance-authority";

import type { AvatarSize, AvatarTone } from "./Avatar.types";

export const avatarStyles = {
  root: cva(
    "relative flex shrink-0 items-center justify-center overflow-hidden",
    {
      variants: {
        tone: {
          neutral: "bg-[var(--fw-color-surface-neutral-secondary)]",
          brand: "bg-[var(--fw-color-surface-brand-tertiary)]",
          success: "bg-[var(--fw-color-surface-success-tertiary)]",
          critical: "bg-[var(--fw-color-surface-critical-tertiary)]",
        },
        size: {
          xs: "size-[var(--fw-size-control-xs)]",
          xssm: "size-[var(--fw-size-control-sm)]",
          sm: "size-[var(--fw-size-control-lg)]",
          md: "size-[var(--fw-size-control-xxxxl)]",
          /* xl(104)은 `size.control` 스케일에 없다(최대 80) — 토큰을 거치지 않는 유일한 값 */
          xl: "size-[104px]",
        },
        radius: {
          circle: "rounded-[var(--fw-radius-full)]",
          square: "",
        },
      },
      compoundVariants: [
        {
          radius: "square",
          size: "xs",
          class: "rounded-[var(--fw-radius-xs)]",
        },
        {
          radius: "square",
          size: "xssm",
          class: "rounded-[var(--fw-radius-xs)]",
        },
        {
          radius: "square",
          size: "sm",
          class: "rounded-[var(--fw-radius-sm)]",
        },
        {
          radius: "square",
          size: "md",
          class: "rounded-[var(--fw-radius-md)]",
        },
        {
          radius: "square",
          size: "xl",
          class: "rounded-[var(--fw-radius-lg)]",
        },
      ],
      defaultVariants: { tone: "neutral", size: "md", radius: "circle" },
    },
  ),
  /**
   * 이미지는 상자를 꽉 채운다. `root` 가 글자·아이콘을 가운데 두려고 flex 정렬을 갖는데,
   * 그러면 이미지 폭이 내용 크기로 줄어 보이지 않는다. 절대 위치로 네 변을 잡는다.
   */
  image:
    "absolute inset-0 [&>*]:size-full [&_img]:size-full [&_img]:object-cover",
  initial: cva("truncate", {
    variants: {
      tone: {
        neutral: "text-[var(--fw-color-content-neutral-primary)]",
        brand: "text-[var(--fw-color-content-brand-primary)]",
        success: "text-[var(--fw-color-content-success-primary)]",
        critical: "text-[var(--fw-color-content-critical-primary)]",
      },
      size: {
        xs: "fw-l2",
        xssm: "fw-l2",
        sm: "fw-h3",
        md: "fw-h3",
        xl: "fw-h3",
      },
    },
    defaultVariants: { tone: "neutral", size: "md" },
  }),
};

/** 아이콘은 xs 만 작고 나머지는 같다 — 상자가 커져도 내용물은 키우지 않는다 */
export const AVATAR_ICON_SIZE: Record<AvatarSize, number> = {
  xs: 12,
  xssm: 12,
  sm: 24,
  md: 24,
  xl: 24,
};

/** 톤은 배경과 글리프를 함께 정한다 — 배경만 물들이면 글리프가 그 위에 떠 보인다 */
export const AVATAR_ICON_COLOR: Record<AvatarTone, string> = {
  neutral: "var(--fw-color-content-neutral-primary)",
  brand: "var(--fw-color-content-brand-primary)",
  success: "var(--fw-color-content-success-primary)",
  critical: "var(--fw-color-content-critical-primary)",
};
